import React, { useState } from "react";
import logo from "../assets/logo.png";
import bgHero from "../assets/First-Responders-hero-img.png";
import respondercalm from "../assets/respondercalm.JPG";
import checklist from "../assets/checklistimg.JPG";
import parafire from "../assets/paramedics-firefighters.JPG"; // placeholder thumb

// Redirect target = your existing thank-you / $7 challenge page
const THANK_YOU_URL = "/";

export default function FreeResetGuide() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [existsMsg, setExistsMsg] = useState(""); // NEW

  // small helper to collect UTM params
  const getUtm = () => {
    const p = new URLSearchParams(window.location.search || "");
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
    const out = {};
    keys.forEach((k) => {
      const v = p.get(k);
      if (v) out[k] = v;
    });
    return out;
  };

  const base = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8080";

  // NEW: ask server if email exists
  const checkEmailExists = async (addr) => {
    try {
      const res = await fetch(`${base}/api/auth/check-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: addr }),
      });
      if (!res.ok) return { exists: false }; // fail-open
      const data = await res.json();
      return { exists: !!data.exists };
    } catch {
      return { exists: false }; // fail-open on network error
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setExistsMsg("");

    if (!firstName.trim()) return setErr("Please enter your first name.");
    if (!/^\S+@\S+\.\S+$/.test(email)) return setErr("Please enter a valid email.");

    const normalizedEmail = email.trim().toLowerCase();
    setBusy(true);

    // 🔎 NEW: block if email already has an account
    const { exists } = await checkEmailExists(normalizedEmail);
    if (exists) {
      setBusy(false);
      setExistsMsg("Looks like you already have an account. Log in to continue.");
      return;
    }

    // store locally for thank-you strip messaging
    try {
      sessionStorage.setItem(
        "frt_free_guide_signup",
        JSON.stringify({ firstName: firstName.trim(), email: normalizedEmail, ts: Date.now() })
      );
    } catch {}

    // fire GA lead event (best effort)
    try {
      window?.gtag?.("event", "generate_lead", { method: "free_reset_guide" });
    } catch {}

    // 🔒 Clear any existing app JWT so this new flow can't reuse an old login
    try {
      localStorage.removeItem("token");
    } catch {}

    // 🧹 start clean — clear any old cookies on the server side
    try {
      await fetch(`${base}/api/funnel/start-clean`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      // non-fatal — continue the flow
      console.warn("start-clean failed (continuing):", e);
    }

    // ✅ Tell backend about the opt-in so it can upsert the user and set frt_uid cookie
    try {
      await fetch(`${base}/api/funnel/guide-optin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // IMPORTANT: lets server set httpOnly frt_uid cookie
        body: JSON.stringify({
          firstName: firstName.trim(),
          email: normalizedEmail,
          utm: getUtm(),
          referrer: document.referrer || "",
          source: "freeresetguide",
        }),
      });
      // even if this fails, we still move user forward; cookie just won't be present
    } catch (e) {
      console.error("guide-optin failed (continuing):", e);
    }

    // go to offer page
    window.location.assign(THANK_YOU_URL);
  };

  // scroll-to-top for secondary CTAs
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-white">
      {/* background + soft gradient */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <img
          src={bgHero}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover object-center opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-white/30" />
      </div>

      {/* HEADER — minimal, no nav */}
      <header className="sticky top-0 z-20 w-full bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/50">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-3 sm:py-4">
          <div className="flex items-center gap-2">
            <img src={logo} alt="First Response Therapy" className="h-8 w-auto" />
            <span className="sr-only">First Response Therapy</span>
          </div>
          <div className="text-xs sm:text-sm text-gray-700">Peer-driven resilience. Built with responders.</div>
        </div>
      </header>

      {/* HERO */}
      <main className="flex items-center">
        <div className="mx-auto w-full max-w-6xl px-4 py-10 lg:py-16">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            {/* Left: Copy + Form */}
            <div>
              <h1 className="text-3xl font-extrabold leading-tight sm:text-4xl">
                Free Reset Guide for First Responders
              </h1>
              <p className="mt-3 text-lg text-gray-700 sm:text-xl">
                7 easy tools you can use after shift to calm your mind and reset — delivered as a
                simple PDF + email instructions.
              </p>

              <ul className="mt-6 space-y-2 text-gray-800">
                <li>🚒 Built for First Responders, Military, and Families</li>
                <li>🧠 Each tool takes under 10 minutes</li>
                <li>💪 Helps you decompress, refocus, and sleep better</li>
              </ul>

              {/* Added hand-off copy so users know the next step */}
              <div className="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700 border">
                You’ll get the full plan in the PDF. On the next page, you can get a
                <strong> Guided Reset</strong> for easy tracking within your personal dashboard — so you actually finish it.
              </div>

              <form onSubmit={handleSubmit} className="mt-6 max-w-md space-y-3">
                <div>
                  <label htmlFor="first" className="sr-only">First Name</label>
                  <input
                    id="first"
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white/90 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-black"
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="sr-only">Email</label>
                  <input
                    id="email"
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white/90 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-black"
                    autoComplete="email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-lg bg-[#e53935] px-6 py-3 font-semibold text-white hover:bg-black disabled:opacity-60"
                >
                  {busy ? "Sending…" : "Send Me the Free Reset Guide →"}
                </button>

                {err && <p className="text-sm text-red-600">{err}</p>}

                {/* NEW: exists message */}
                {existsMsg && (
                  <div className="text-sm rounded-md border border-red-200 bg-red-50 p-3 text-red-700">
                    {existsMsg}{" "}
                    <a href="/login" className="underline font-medium hover:text-red-800">Log in</a>
                    {" "}or use a different email.
                  </div>
                )}

                <p className="text-xs text-gray-600">Just practical mind + body calming tools sent by email.</p>
                {/* Always-visible helper */}
              </form>
            </div>

            {/* Right: Visuals — show as guide mockups */}
            <div className="flex items-center justify-center">
              <div className="relative w-full max-w-md">
                <div className="rounded-2xl border border-gray-200 bg-white/80 p-3 shadow-lg">
                  <img
                    src={checklist}
                    alt="Pages from the printable Reset Guide"
                    className="h-72 w-full rounded-xl object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="absolute -right-6 bottom-[-20px] w-36 rotate-3 rounded-2xl border border-gray-200 bg-white/80 p-2 shadow-xl">
                  <img
                    src={respondercalm}
                    alt="Pocket version of the Reset Guide"
                    className="h-48 w-full rounded-xl object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Credibility strip */}
          <div className="mt-12">
            <div className="mx-auto max-w-5xl rounded-xl border bg-white/70 p-4 text-center shadow-sm">
              <div className="flex items-center justify-center gap-4 opacity-70">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-6 w-16 rounded bg-gray-200" />
                ))}
              </div>
              <p className="mt-3 text-sm text-gray-700">Built with the input of active first responders and licensed therapists.</p>
            </div>
          </div>

          {/* What’s inside */}
          <section className="mt-16">
            <h2 className="text-center text-2xl font-bold">What’s Inside the Free Reset Guide</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[
                ["🧘", "Daily reset method to calm your nervous system"],
                ["💬", "Reflection prompts to process high-stress calls"],
                ["💤", "Sleep reset routine to recover faster"],
              ].map(([icon, text], i) => (
                <div key={i} className="rounded-lg border bg-white p-6 text-center shadow-sm">
                  <div className="mb-2 text-2xl">{icon}</div>
                  <p className="text-gray-800">{text}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <button
                onClick={scrollToTop}
                disabled={busy}
                className="rounded-lg bg-[#e53935] px-6 py-3 font-semibold text-white hover:bg-black disabled:opacity-60"
              >
                {busy ? "Sending…" : "Send Me the Guide →"}
              </button>
              <p className="mt-2 text-sm text-gray-600">After you get the Free Reset Guide, you can immediately start recalibrating at your own pace.</p>
            </div>
          </section>

          {/* Short video (optional) */}
          <section className="mt-16">
            <div className="mx-auto max-w-3xl">
              <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
                {/* replace with real <video> when ready */}
                <img
                  src={parafire}
                  alt="Video introduction to the Reset Guide"
                  className="h-64 w-full object-cover"
                  loading="lazy"
                />
              </div>
              <p className="mt-3 text-center text-sm text-gray-700">
                “I made this guide to help responders decompress and recover faster — start with these 7 easy days.”
              </p>
            </div>
          </section>

          {/* Testimonials */}
          <section className="mt-16">
            <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2">
              {[
                ["Finally, something that makes sense for us.", "— Firefighter, Ohio"],
                ["Used one tool from the guide and slept through the night.", "— LEO, Texas"],
              ].map(([quote, who], i) => (
                <div key={i} className="rounded-lg border bg-white p-6 shadow-sm">
                  <div className="text-xl">💬</div>
                  <p className="mt-2 italic text-gray-900">“{quote}”</p>
                  <p className="mt-1 text-sm text-gray-600">{who}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Final CTA */}
          <section className="mt-16 rounded-2xl bg-[#3935e5] px-6 py-12 text-center text-white">
            <h3 className="text-2xl font-bold">Start Your Reset Today — It’s Free.</h3>
            <button
              onClick={scrollToTop}
              disabled={busy}
              className="mt-4 rounded-lg bg-[#e53935] px-8 py-3 font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              {busy ? "Sending…" : "Send Me the Free Reset Guide →"}
            </button>
            <p className="mt-2 text-sm text-white/90">Takes 10 seconds to sign up. The guide arrives instantly by email.</p>
          </section>

          {/* Footer */}
          <footer className="mt-12 border-t pt-6 text-center text-sm text-gray-600">
            © {new Date().getFullYear()} First Response Therapy ·{" "}
          </footer>
        </div>
      </main>
    </div>
  );
}